import type { ViewController } from '@/engine/app/View.controller';
import type { ViewsService } from '@/engine/app/Views.service';
import { ClippingManager } from '../../../clipping/ClippingManager';
import { TYPES } from '@/engine/ioc';
import { inject, injectable } from 'inversify';
import type {
  IImageViewPortDetails,
  ViewEntity,
  ViewHelper,
  ViewPortInfo
} from '@/engine/app/models/viewEntity/view.entity';
import { ViewPortKind, type IImageSliceViewModel } from '@/engine/app/interfaces';
import type { IImageViewProps } from '../../views/ImageViewPortWrapper';
import type { IThreeDViewProps } from '../../views/types';
import type { QUADRO_LAYOUT_PLACEHOLDERS } from './AccentedQudroViewLayout';
import { action, makeObservable, observable, reaction } from 'mobx';

export type LayoutPlaceholderDescriptorType = (typeof QUADRO_LAYOUT_PLACEHOLDERS)[number];

@injectable()
export class AccentedQuadroViewLayoutController {
  private readonly _viewController: ViewController;

  private readonly _viewService: ViewsService;

  private readonly _clippingManager: ClippingManager;

  @observable
  private _layoutDescriptor2ViewDescriptorMap: Map<LayoutPlaceholderDescriptorType, string> = new Map();

  @observable
  private _isSidebarCollapsed = false;

  public constructor(
    @inject(TYPES.ViewController) viewController: ViewController,
    @inject(TYPES.ViewsService) ViewsService: ViewsService
  ) {
    this._viewController = viewController;
    this._viewService = ViewsService;
    this._clippingManager = new ClippingManager(this._viewService);

    this._initiateLayoutDescriptor2ViewDescriptorMap();
    this._initSubscriptions();
    this._setSidebarForView(this._viewService.currentView);

    makeObservable(this);
  }

  public get viewPorts(): Array<ViewPortInfo> {
    return this._viewController.getViewPorts();
  }

  public get imageSlices(): Array<IImageSliceViewModel> {
    return this._viewController.getImageSlices();
  }

  public get currentView(): ViewEntity {
    const currentView = this._viewService.currentView;
    if (!currentView) throw new Error('Current view is null');
    return currentView;
  }

  public get helpers(): Array<ViewHelper> {
    return this.currentView.getHelpers();
  }

  public get clippingManager(): ClippingManager {
    return this._clippingManager;
  }

  public get imageViews(): Array<IImageViewProps> {
    return this.viewPorts
      .filter((vp) => vp.kind == ViewPortKind.ImageSlice)
      .map((vp) => {
        const sliceDescriptor = (vp.details as IImageViewPortDetails).sliceDescriptor;
        const imgSlice = this.imageSlices.find((is) => is.descriptor == sliceDescriptor);
        if (!imgSlice) {
          throw new Error('Slice is not found');
        }
        return {
          descriptor: vp.descriptor,
          borderColor: imgSlice.borderColor,
          sliceType: sliceDescriptor
        };
      });
  }

  public get threeDViews(): Array<IThreeDViewProps> {
    return this.viewPorts
      .filter((vp) => vp.kind == ViewPortKind.ThreeD)
      .map((vp) => {
        return {
          descriptor: vp.descriptor
        };
      });
  }

  public get isSidebarCollapsed(): boolean {
    return this._isSidebarCollapsed;
  }

  public get viewOrientationDescriptor(): string {
    return this.currentView.getViewOrientationDescriptor();
  }

  @action
  public setSidebarCollapsed(isCollapsed: boolean): void {
    this._isSidebarCollapsed = isCollapsed;
  }

  public getContentDescriptorByLayoutDescriptor(layoutDescriptor: LayoutPlaceholderDescriptorType): string {
    return this._layoutDescriptor2ViewDescriptorMap.get(layoutDescriptor) ?? '';
  }

  public getLayoutPlaceholderDescriptorByContentDescriptor(
    contentDescriptor: string
  ): LayoutPlaceholderDescriptorType | undefined {
    return Array.from(this._layoutDescriptor2ViewDescriptorMap.entries()).find(
      ([, value]) => value == contentDescriptor
    )?.[0] as LayoutPlaceholderDescriptorType;
  }

  @action
  public swapViews(descriptor1: LayoutPlaceholderDescriptorType, descriptor2: LayoutPlaceholderDescriptorType): void {
    if (descriptor1 == descriptor2) return;
    const currentContentDescriptor1 = this._layoutDescriptor2ViewDescriptorMap.get(descriptor1);
    const currentContentDescriptor2 = this._layoutDescriptor2ViewDescriptorMap.get(descriptor2);

    if (!currentContentDescriptor1 || !currentContentDescriptor2) return;

    this._layoutDescriptor2ViewDescriptorMap.set(descriptor1, currentContentDescriptor2);
    this._layoutDescriptor2ViewDescriptorMap.set(descriptor2, currentContentDescriptor1);
  }

  public maximizeView(layoutDescriptor: LayoutPlaceholderDescriptorType): void {
    const current3DViewPlaceholder = this.getLayoutPlaceholderDescriptorByContentDescriptor('3dView');
    if (!current3DViewPlaceholder) return;
    this.swapViews(current3DViewPlaceholder, 'main');
    if (layoutDescriptor == current3DViewPlaceholder) return;
    this.swapViews(layoutDescriptor, 'main');
  }

  private _initiateLayoutDescriptor2ViewDescriptorMap(): void {
    this._layoutDescriptor2ViewDescriptorMap.set('topLeft', 'coronalView');
    this._layoutDescriptor2ViewDescriptorMap.set('middleLeft', 'axialView');
    this._layoutDescriptor2ViewDescriptorMap.set('bottomLeft', 'sagittalView');
    this._layoutDescriptor2ViewDescriptorMap.set('main', '3dView');
  }

  private _setSidebarForView(view: ViewEntity | null): void {
    if (!view) return;
    this.setSidebarCollapsed(!!view.collapseImages);
  }

  private _initSubscriptions(): void {
    reaction(
      () => this._viewService.currentView,
      (currentView): void => {
        this._setSidebarForView(currentView);
      }
    );
  }
}
